import type { Maybe } from '@adornis/base/utilTypes.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-tailwind-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { CourseDigital } from '@chancenwerk/shared/courses/CourseDigital.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { switchMap } from 'rxjs';
import { BoardRoutes } from '../routes.js';

/**
 * @customElement board-component-course-digital-card
 */
@customElement('board-component-course-digital-card')
export class BoardComponentCourseDigitalCard extends ChemistryLitElement {
  @property({ attribute: false }) course = new RXController<Maybe<CourseDigital>>(this, null);

  @state() private readonly _openStudents = new RXController(
    this,
    this.course.observable.pipe(
      switchMap(async c => {
        if (!c) {
          return 0;
        }
        const num = await CourseDigital.getDigitalCourseOpenStudents(c._id)();
        return num;
      }),
    ),
  );

  override styles() {
    return [
      ...super.styles(),
      {
        ':host > cr-card:hover': {
          boxShadow: `inset 0 0 0 3px ${this.colors.accent}`,
        },
        ':host > cr-card': {
          cursor: 'pointer',
          backgroundColor: this.course.value?.changeNotification ? this.colors.tone.error.shade(100) : '',
        },
      },
    ];
  }

  private _changed() {
    if (!this.course.value?.changeNotification) {
      return nothing;
    }

    return html`<cr-icon ${css({ color: this.colors.tone.error, fontSize: '1.4rem' })}>
      report_problem
      <cr-tooltip> <cr-text> Es gab eine Änderung </cr-text> </cr-tooltip>
    </cr-icon>`;
  }

  override render() {
    if (!this.course.value) {
      return;
    }

    return html`<cr-card
      wrap
      space="md"
      @click=${() => goTo(`${BoardRoutes.details}/${CourseDigital._class}/${this.course.value._id}`)}
    >
      <cr-flex space="md" space-between>
        <cr-flex space="sm">
          <cr-flex space-between horizontal w100>
            <cr-flex horizontal crossaxis-center space="md">
              ${this._changed()}
              <cr-flex space="sm">
                <cr-flex start crossaxis-center horizontal space="md">
                  ${this.course.value.name && this.course.value.name.length > 0
                    ? html`<cr-text bold> ${this.course.value.name} </cr-text>`
                    : html`<cr-text bold> Dig. Intensivkurs </cr-text>`}
                </cr-flex>
                <cr-flex horizontal space="sm">
                  ${this.course.value.subject ? html`<cr-text> ${this.course.value.subject} </cr-text>` : nothing}
                  ${this.course.value.jahrgangsstufe
                    ? html`<cr-text> Stufe ${this.course.value.jahrgangsstufe} </cr-text>`
                    : nothing}
                </cr-flex>
              </cr-flex>
            </cr-flex>
            ${this._openStudents.value > 0
              ? html`<cr-flex horizontal crossaxis-center ${css({ alignSelf: 'start', fontSize: '1.4rem' })}>
                  <cr-tooltip><cr-text>Offene Jugendliche mit diesem Wunschfach</cr-text></cr-tooltip>
                  <cr-icon>emoji_people</cr-icon><cr-text>${this._openStudents.value}</cr-text>
                </cr-flex>`
              : nothing}
          </cr-flex>
          <cr-flex space="sm" ${css({ marginTop: this.spacing.md })}>
            <cr-text bold> Wann: </cr-text>
            <cr-flex space="md" vertical>
              <cr-flex space="sm">
                ${this.course.value.weekday
                  ? html` <cr-text> ${this.course.value.weekday} </cr-text> `
                  : html` <cr-text bold ${css({ color: this.colors.tone.error })}> Tag ? </cr-text> `}
                ${this.course.value.startTime && this.course.value.endTime
                  ? html`
                      <cr-text>
                        ${this.course.value.startTime.toFormat('HH:mm')} -
                        ${this.course.value.endTime.toFormat('HH:mm')}
                      </cr-text>
                    `
                  : html` <cr-text bold ${css({ color: this.colors.tone.error })}> Uhrzeit ? </cr-text> `}
              </cr-flex>
            </cr-flex>
          </cr-flex>
        </cr-flex>
      </cr-flex>
    </cr-card>`;
  }
}
